import { trackEvent } from '@/utils/analytics'
import { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from '@/i18n'
import { useAppStore } from '@/store'
import { toast } from 'sonner'
import { OrganizationUser, formatUserRole, type UserRole } from '@/types/users'
import { formatDateTime } from '@/utils/timeUtils'
import { Search, Plus, MoreVertical, Loader2 } from 'lucide-react'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import { IntuiInput } from '@/components/intueri/IntuiInput'
import { IntuiBadge } from '@/components/intueri/IntuiBadge'
import {
  IntuiSelect, IntuiSelectContent, IntuiSelectItem,
  IntuiSelectTrigger, IntuiSelectValue,
} from '@/components/intueri/IntuiSelect'
import {
  IntuiTabs, IntuiTabsList, IntuiTabsTrigger, IntuiTabsContent,
} from '@/components/intueri/IntuiTabs'
import {
  IntuiDropdownMenu, IntuiDropdownMenuContent,
  IntuiDropdownMenuItem, IntuiDropdownMenuTrigger,
} from '@/components/intueri/IntuiDropdownMenu'
import InviteUserModal from './InviteUserModal'
import EditUserModal from './EditUserModal'
import RevokeInviteDialog from './RevokeInviteDialog'

const PERMISSIONS = {
  Content: ['View', 'Create', 'Edit', 'Delete', 'Publish'],
  Team: ['View members', 'Invite', 'Remove', 'Edit roles'],
  Settings: ['View', 'Edit'],
}

// Maps the permission-matrix data keys (stable identifiers) to their i18n keys.
const GROUP_LABEL_KEYS: Record<string, string> = {
  Content: 'settings:team.permissions.groups.content',
  Team: 'settings:team.permissions.groups.team',
  Settings: 'settings:team.permissions.groups.settings',
}
const PERM_LABEL_KEYS: Record<string, string> = {
  View: 'settings:team.permissions.perms.view',
  Create: 'settings:team.permissions.perms.create',
  Edit: 'settings:team.permissions.perms.edit',
  Delete: 'settings:team.permissions.perms.delete',
  Publish: 'settings:team.permissions.perms.publish',
  'View members': 'settings:team.permissions.perms.viewMembers',
  Invite: 'settings:team.permissions.perms.invite',
  Remove: 'settings:team.permissions.perms.remove',
  'Edit roles': 'settings:team.permissions.perms.editRoles',
}

const DEFAULT_PERMS: Record<string, Record<string, boolean>> = {
  Owner: { View: true, Create: true, Edit: true, Delete: true, Publish: true, 'View members': true, Invite: true, Remove: true, 'Edit roles': true },
  Admin: { View: true, Create: true, Edit: true, Delete: true, Publish: true, 'View members': true, Invite: true, Remove: true, 'Edit roles': true },
  Member: { View: true, Create: true, Edit: true, Delete: false, Publish: true, 'View members': true, Invite: false, Remove: false, 'Edit roles': false },
}

export default function TeamV2() {
  const { t } = useTranslation()
  const {
    fetchOrganizationUsers, inviteUserToOrganization, deleteOrganizationUser,
    updateUserRole, revokeOrganizationInvite, organizationUsers, isUsersLoading, user,
  } = useAppStore()
  const isAdmin = user?.role === 'admin'
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showRevokeModal, setShowRevokeModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<OrganizationUser | null>(null)
  const [selectedInvite, setSelectedInvite] = useState<OrganizationUser | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  const filteredUsers = useMemo(() => {
    let users = organizationUsers
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase()
      users = users.filter((u) => u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q))
    }
    if (statusFilter !== 'all') {
      users = users.filter((u) => u.status === statusFilter)
    }
    return users
  }, [organizationUsers, searchTerm, statusFilter])

  useEffect(() => { fetchOrganizationUsers() }, [fetchOrganizationUsers])

  const handleInviteUser = async (data: { email: string; role: UserRole }) => {
    await inviteUserToOrganization(data)
    trackEvent('settings-team_member_invited', { role: data.role, invite_source: 'direct' })
  }

  const handleRevokeInvite = async () => {
    if (!selectedInvite?.invite_id) { toast.error(t('settings:team.errors.userNotFound', { defaultValue: 'User not found' })); return }
    try { await revokeOrganizationInvite(selectedInvite.invite_id); setShowRevokeModal(false); setSelectedInvite(null) } catch { /* */ }
  }

  return (
    <div>
      <IntuiTabs defaultValue="members">
        <IntuiTabsList variant="line" className="mb-6">
          <IntuiTabsTrigger value="members">{t('settings:team.tabs.members', { defaultValue: 'Team member' })}</IntuiTabsTrigger>
          {isAdmin && <IntuiTabsTrigger value="permissions">{t('settings:team.tabs.permissions', { defaultValue: 'Roles & Permissions' })}</IntuiTabsTrigger>}
        </IntuiTabsList>

        <IntuiTabsContent value="members">
          {/* Header */}
          <h3 className="text-base font-semibold mb-4">{t('settings:team.membersCount', { total: organizationUsers.length, defaultValue: 'Members ({{total}})' })}</h3>

          {/* Toolbar */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="relative w-[240px]">
                <Search className="absolute start-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                <IntuiInput
                  placeholder={t('settings:team.searchPlaceholder', { defaultValue: 'Search' })}
                  value={searchTerm}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                  className="h-9 ps-9"
                />
              </div>
              <IntuiSelect value={statusFilter} onValueChange={setStatusFilter}>
                <IntuiSelectTrigger className="w-[140px] h-9">
                  <IntuiSelectValue />
                </IntuiSelectTrigger>
                <IntuiSelectContent>
                  <IntuiSelectItem value="all">{t('settings:team.status.all', { defaultValue: 'All Status' })}</IntuiSelectItem>
                  <IntuiSelectItem value="active">{t('settings:team.status.active', { defaultValue: 'Active' })}</IntuiSelectItem>
                  <IntuiSelectItem value="pending">{t('settings:team.status.invited', { defaultValue: 'Invited' })}</IntuiSelectItem>
                </IntuiSelectContent>
              </IntuiSelect>
            </div>
            {isAdmin && (
              <IntuiButton size="lg" leftIcon={<Plus />} onClick={() => setShowInviteModal(true)}>
                {t('settings:team.inviteMember', { defaultValue: 'Invite Member' })}
              </IntuiButton>
            )}
          </div>

          {/* Table */}
          {isUsersLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="w-full rounded-lg border border-[#e5e5e5] dark:border-dark-border overflow-hidden">
              <div className="flex items-center border-b border-[#e5e5e5] dark:border-dark-border bg-muted/30">
                <span className="w-[200px] px-2 py-4 text-sm font-medium text-muted-foreground">{t('settings:team.tableHeaders.name', { defaultValue: 'Name' })}</span>
                <span className="flex-1 px-2 py-4 text-sm font-medium text-muted-foreground">{t('settings:team.tableHeaders.email', { defaultValue: 'Email' })}</span>
                <span className="w-[100px] px-2 py-4 text-sm font-medium text-muted-foreground">{t('settings:team.tableHeaders.role', { defaultValue: 'Role' })}</span>
                <span className="w-[80px] px-2 py-4 text-sm font-medium text-muted-foreground">{t('settings:team.tableHeaders.status', { defaultValue: 'Status' })}</span>
                <span className="w-[160px] px-2 py-4 text-sm font-medium text-muted-foreground">{t('settings:team.tableHeaders.addedOn', { defaultValue: 'Added on' })}</span>
                <span className="w-9" />
              </div>
              {filteredUsers.map((u) => (
                <div key={u.user_id || u.id} className="flex items-center h-14 border-b border-[#e5e5e5] dark:border-dark-border hover:bg-muted/50 transition-colors">
                  <span className="w-[200px] px-2 text-sm font-medium truncate">{u.name || t('settings:team.notAvailable', { defaultValue: 'N/A' })}</span>
                  <span className="flex-1 px-2 text-sm text-muted-foreground truncate">{u.email || t('settings:team.notAvailable', { defaultValue: 'N/A' })}</span>
                  <span className="w-[100px] px-2 text-sm text-muted-foreground">{formatUserRole(u.role)}</span>
                  <div className="w-[80px] px-2">
                    <IntuiBadge variant={u.status === 'active' ? 'success' : 'secondary'}>
                      {u.status === 'active' ? t('settings:team.status.active', { defaultValue: 'Active' }) : t('settings:team.status.invited', { defaultValue: 'Invited' })}
                    </IntuiBadge>
                  </div>
                  <span className="w-[160px] px-2 text-sm text-muted-foreground">{formatDateTime(u.joined_at || '') || t('settings:team.notAvailable', { defaultValue: 'N/A' })}</span>
                  <div className="w-9 flex justify-center">
                    {isAdmin && u.user_id !== user?.userId && (
                      <IntuiDropdownMenu>
                        <IntuiDropdownMenuTrigger asChild>
                          <IntuiButton variant="ghost" size="icon-sm"><MoreVertical className="size-4" /></IntuiButton>
                        </IntuiDropdownMenuTrigger>
                        <IntuiDropdownMenuContent align="end">
                          {u.status === 'pending' ? (
                            <IntuiDropdownMenuItem variant="destructive" onClick={() => { setSelectedInvite(u); setShowRevokeModal(true) }}>
                              {t('settings:team.actions.revokeInvite', { defaultValue: 'Revoke Invite' })}
                            </IntuiDropdownMenuItem>
                          ) : (
                            <>
                              <IntuiDropdownMenuItem onClick={() => { setSelectedUser(u); setShowEditModal(true) }}>
                                {t('settings:team.actions.edit', { defaultValue: 'Edit' })}
                              </IntuiDropdownMenuItem>
                              <IntuiDropdownMenuItem variant="destructive" onClick={async () => {
                                if (u.user_id) { const ok = await deleteOrganizationUser(u.user_id); if (ok) trackEvent('settings-team_member_removed', {}) }
                              }}>
                                {t('settings:team.actions.delete', { defaultValue: 'Delete' })}
                              </IntuiDropdownMenuItem>
                            </>
                          )}
                        </IntuiDropdownMenuContent>
                      </IntuiDropdownMenu>
                    )}
                  </div>
                </div>
              ))}
              {filteredUsers.length === 0 && (
                <div className="py-12 text-center text-sm text-muted-foreground">{t('settings:team.noMembersFound', { defaultValue: 'No members found' })}</div>
              )}
            </div>
          )}
        </IntuiTabsContent>

        <IntuiTabsContent value="permissions">
          {/* Roles & Permissions matrix — one-liner explains what the tab is
              for; the bare matrix wasn't self-explanatory. */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <p className="text-sm text-muted-foreground">
              {t('settings:team.permissions.subtitle', { defaultValue: 'Configure what each role in your organization can view and do.' })}
            </p>
            <IntuiButton onClick={() => toast.success(t('settings:team.permissions.savedToast', { defaultValue: 'Roles and permission updated successfully' }))}>
              {t('settings:team.permissions.saveChanges', { defaultValue: 'Save Changes' })}
            </IntuiButton>
          </div>
          <div className="w-full border border-[#e5e5e5] dark:border-dark-border rounded-lg overflow-hidden">
            {/* Header */}
            <div className="flex items-center bg-muted/50">
              <span className="flex-1 px-4 py-3 text-sm font-medium text-muted-foreground">{t('settings:team.permissions.columns.permission', { defaultValue: 'Permission' })}</span>
              <span className="w-[100px] px-4 py-3 text-sm font-medium text-muted-foreground text-center">{t('settings:team.permissions.roles.owner', { defaultValue: 'Owner' })}</span>
              <span className="w-[100px] px-4 py-3 text-sm font-medium text-muted-foreground text-center">{t('settings:team.permissions.roles.admin', { defaultValue: 'Admin' })}</span>
              <span className="w-[100px] px-4 py-3 text-sm font-medium text-muted-foreground text-center">{t('settings:team.permissions.roles.member', { defaultValue: 'Member' })}</span>
            </div>
            {Object.entries(PERMISSIONS).map(([group, perms]) => (
              <div key={group}>
                <div className="flex items-center bg-muted/30 border-t border-[#e5e5e5] dark:border-dark-border">
                  <span className="flex-1 px-4 py-3 text-sm font-semibold">{t(GROUP_LABEL_KEYS[group], { defaultValue: group })}</span>
                  <span className="w-[100px]" /><span className="w-[100px]" /><span className="w-[100px]" />
                </div>
                {perms.map((perm) => (
                  <div key={perm} className="flex items-center border-t border-[#e5e5e5] dark:border-dark-border">
                    <span className="flex-1 px-4 py-3 text-sm">{PERM_LABEL_KEYS[perm] ? t(PERM_LABEL_KEYS[perm], { defaultValue: perm }) : perm}</span>
                    {['Owner', 'Admin', 'Member'].map((role) => (
                      <div key={role} className="w-[100px] flex justify-center py-3">
                        <input
                          type="checkbox"
                          defaultChecked={DEFAULT_PERMS[role]?.[perm] ?? false}
                          className="size-4 rounded border-input accent-[#0e7490]"
                          disabled={role === 'Owner'}
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </IntuiTabsContent>
      </IntuiTabs>

      {/* Modals */}
      {isAdmin && (
        <>
          <InviteUserModal isOpen={showInviteModal} onClose={() => setShowInviteModal(false)} onSubmit={handleInviteUser} />
          <EditUserModal isOpen={showEditModal} onClose={() => { setShowEditModal(false); setSelectedUser(null) }} onSubmit={updateUserRole} selectedUser={selectedUser} />
          <RevokeInviteDialog isOpen={showRevokeModal} email={selectedInvite?.email || ''} onClose={() => { setShowRevokeModal(false); setSelectedInvite(null) }} onConfirm={handleRevokeInvite} />
        </>
      )}
    </div>
  )
}
